/**
 * Date utilities
 * Pure functions for date operations
 */

/**
 * Format date to ISO string (YYYY-MM-DD)
 */
export function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Parse ISO date string to Date object
 */
export function parseDate(dateString: string): Date {
  return new Date(dateString);
}

/**
 * Get start of day
 */
export function getStartOfDay(date: Date): Date {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  return result;
}

/**
 * Get end of day
 */
export function getEndOfDay(date: Date): Date {
  const result = new Date(date);
  result.setHours(23, 59, 59, 999);
  return result;
}

/**
 * Check if date is today
 */
export function isToday(date: Date): boolean {
  const today = new Date();
  return formatDate(date) === formatDate(today);
}

/**
 * Check if date is in the past
 */
export function isPast(date: Date): boolean {
  return date < new Date();
}

/**
 * Check if date is in the future
 */
export function isFuture(date: Date): boolean {
  return date > new Date();
}

/**
 * Get days between two dates
 */
export function getDaysBetween(startDate: Date, endDate: Date): number {
  const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Add days to date
 */
export function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/**
 * Check if date is within date range
 */
export function isDateInRange(
  date: Date,
  startDate: Date,
  endDate: Date
): boolean {
  return date >= startDate && date <= endDate;
}

/**
 * Get current date as ISO string
 */
export function getCurrentDateString(): string {
  return formatDate(new Date());
}

/**
 * Validate date string format (YYYY-MM-DD)
 */
export function isValidDateString(dateString: string): boolean {
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(dateString)) return false;

  const date = new Date(dateString);
  return !isNaN(date.getTime());
}

/**
 * Calculate days remaining until a given date
 */
export function getDaysRemaining(endDate: string | null | undefined): number {
  if (!endDate) return 0;

  const end = new Date(endDate);
  const now = new Date();
  const diffTime = end.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return Math.max(0, diffDays);
}

/**
 * Format challenge end date for display
 */
export function formatChallengeEndDate(endDate: string | null | undefined): string {
  if (!endDate) return 'Ongoing';

  const date = new Date(endDate);
  const options: Intl.DateTimeFormatOptions = {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  };
  return date.toLocaleDateString('en-US', options);
}

/**
 * Get challenge time status (days remaining, ended, etc.)
 */
export function getChallengeTimeStatus(
  startDate: string | null | undefined,
  endDate: string | null | undefined
): {
  status: 'upcoming' | 'active' | 'ended' | 'ongoing';
  daysRemaining: number;
  daysSinceStart: number;
  formattedEndDate: string;
  hasValidDates: boolean;
} {
  // If no dates are provided, treat as ongoing
  if (!startDate && !endDate) {
    return {
      status: 'ongoing',
      daysRemaining: 0,
      daysSinceStart: 0,
      formattedEndDate: 'Ongoing',
      hasValidDates: false,
    };
  }

  const now = new Date();
  const start = startDate ? new Date(startDate) : null;
  const end = endDate ? new Date(endDate) : null;

  const daysRemaining = getDaysRemaining(endDate);
  const daysSinceStart = start
    ? Math.floor((now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24))
    : 0;

  let status: 'upcoming' | 'active' | 'ended' | 'ongoing';

  if (!start && !end) {
    status = 'ongoing';
  } else if (start && now < start) {
    status = 'upcoming';
  } else if (end && now > end) {
    status = 'ended';
  } else {
    status = 'active';
  }

  return {
    status,
    daysRemaining,
    daysSinceStart,
    formattedEndDate: formatChallengeEndDate(endDate),
    hasValidDates: !!(startDate || endDate),
  };
}

/**
 * Format relative time (e.g., "2 days ago", "in 3 days")
 */
export function formatRelativeTime(date: string | null | undefined): string {
  if (!date) return 'ongoing';

  const targetDate = new Date(date);
  const now = new Date();
  const diffTime = targetDate.getTime() - now.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return 'today';
  } else if (diffDays === 1) {
    return 'tomorrow';
  } else if (diffDays === -1) {
    return 'yesterday';
  } else if (diffDays > 0) {
    return `in ${diffDays} days`;
  } else {
    return `${Math.abs(diffDays)} days ago`;
  }
}

/**
 * Get a user-friendly message for challenge timing
 */
export function getChallengeTimingMessage(
  startDate: string | null | undefined,
  endDate: string | null | undefined
): string {
  const timeStatus = getChallengeTimeStatus(startDate, endDate);

  if (!timeStatus.hasValidDates) {
    return 'Use common sense - complete daily';
  }

  switch (timeStatus.status) {
    case 'upcoming':
      return `Starts ${formatRelativeTime(startDate)}`;
    case 'active':
      if (timeStatus.daysRemaining > 0) {
        return `${timeStatus.daysRemaining} days remaining`;
      } else {
        return 'Ends today';
      }
    case 'ended':
      return 'Challenge ended';
    case 'ongoing':
      return 'Ongoing challenge';
    default:
      return 'Use common sense - complete daily';
  }
}

